import { Product } from "@/types/types";
import EnergySavingCTA from "./components/EnergySavingCTA/EnergySavingCTA";
import { useEffect, useState } from "react";
import EnergySavingModal from "./components/EnergySavingModal/EnergySavingModal";
import { useVariant } from "@/hooks/useVariant/useVariant";
import fetchEnergyLabels, {
  EnergyLabelsRequestParams,
  EnergyLabelsResponse,
} from "./data/EnergyLabelsFetcher";
import isEmpty from "lodash/isEmpty";
import Cookies from "js-cookie";
import { getProductType, mapRequestParams } from "./utils";

type Props = {
  product: Product;
};

export type EnergyLabelInputValues = {
  [index: string]: number | null;
  customUsageTimesPerPeriod: number | null;
  customUsagePeriod: number | null;
  customEnergyCostYears: number | null;
  customTariff: number | null;
};

type StoredInputValues = {
  [productType: string]: EnergyLabelInputValues;
};

const getStoredProductTypeValues = (
  storedValues: StoredInputValues,
  productType: string,
) => {
  const productTypeValues = storedValues[productType];

  if (productTypeValues) {
    const newValues: EnergyLabelInputValues = {
      customUsageTimesPerPeriod: null,
      customUsagePeriod: null,
      customEnergyCostYears: null,
      customTariff: null,
    };
    for (const property in productTypeValues) {
      newValues[property] = Number(productTypeValues[property]);
    }

    return newValues;
  }

  return null;
};

export const EnergySavingUI = ({ product }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [, setLoading] = useState(false);
  const [previousUsage, setPreviousUsage] =
    useState<EnergyLabelInputValues | null>(null);
  const [, setCustomUsage] = useState<StoredInputValues | null>(null);
  const [updateResponse, setUpdateResponse] = useState<EnergyLabelsResponse>(
    {},
  );

  const { variant } = useVariant(product);
  const { sellingProductType } = product;
  const productType = getProductType(sellingProductType);

  const updateEnergyLabels = (
    requestParams: EnergyLabelsRequestParams | null,
  ) => {
    if (!sellingProductType) return;

    setLoading(true);
    fetchEnergyLabels(sellingProductType, variant.title, requestParams).then(
      (result) => {
        setLoading(false);
        setUpdateResponse(result);
      },
    );
  };

  useEffect(() => {
    if (isEmpty(updateResponse) && sellingProductType) {
      const cookie = Cookies.get("energy-saving");
      let productTypeValues: EnergyLabelInputValues | null = null;

      if (cookie && productType) {
        const storedValues = JSON.parse(cookie) as StoredInputValues;
        productTypeValues = getStoredProductTypeValues(
          storedValues,
          productType,
        );

        if (productTypeValues) setPreviousUsage(productTypeValues);

        setCustomUsage(storedValues);
      }

      const requestParams = mapRequestParams(productTypeValues);
      updateEnergyLabels(requestParams);
    }
  }, [productType, sellingProductType, updateResponse]);

  if (
    !sellingProductType ||
    !productType ||
    !updateResponse ||
    !updateResponse.responseMetaData
  )
    return null;

  const saveUsagePreference = (
    paramName: string,
    paramValue: string | number,
  ) => {
    setCustomUsage((prevState) => {
      const newState = {
        ...prevState,
        [productType]: {
          ...(prevState && prevState[productType]
            ? prevState[productType]
            : {}),
          [paramName]: paramValue,
        },
      } as StoredInputValues;

      Cookies.set("energy-saving", JSON.stringify(newState));

      return newState;
    });
  };

  return (
    <div data-component="EnergySaving">
      <EnergySavingCTA
        onClick={() => setModalOpen(true)}
        energyLabelsResponse={updateResponse}
        variant={variant}
      />
      <EnergySavingModal
        variant={variant}
        sellingProductType={sellingProductType}
        updateEnergyLabels={updateEnergyLabels}
        updateResponse={updateResponse}
        onClose={() => setModalOpen(false)}
        isOpen={modalOpen}
        onUpdateUsageParams={saveUsagePreference}
        previousUsageParams={previousUsage}
      />
    </div>
  );
};
